import React, { FC, useEffect } from 'react';
import { Col, Input, Row, Switch, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Form, IndividualDocumentTypeInput, LocationInput, TerminalInput } from '~/components';
import { Employee } from '~/types';
import { phoneValidation } from '~/utils';
import { employees } from '~/routes';
import PhoneInput from '~/components/PhoneInput';
import EmployeeInput from '~/components/inputs/EmployeeInput';
import useIsLocationMandatory from '~/hooks/useIsLocationMandatory';
import { useWatch } from 'antd/es/form/Form';
import RolesInput from '~/components/RolesInput';
import { useIsManager, useIsSelf } from '~/hooks/identity';

interface EmployeeFormProps {
  name: typeof employees;
  initialValues?: Employee;
  edit?: boolean;
}

const EmployeeForm: FC<EmployeeFormProps> = (props) => {
  const { name, initialValues, edit } = props;
  const [t] = useTranslation('employees');
  const dt = (key: string): string => t(`translation:${key}`);
  const [form] = Form.useForm();
  const isLocationMandatory = useIsLocationMandatory();
  const selectedRoles = useWatch('roles', form);
  const selectedCashierOrAgent =
    selectedRoles?.includes('BUSINESS_CASHIER') || selectedRoles?.includes('BUSINESS_SALES_AGENT');
  const selectedManager = selectedRoles?.includes('BUSINESS_MANAGER');
  // Clear deselected role's location.
  useEffect(() => {
    if (selectedRoles && !selectedCashierOrAgent) {
      form.setFieldValue('workLocationId', '');
      form.setFieldValue('terminalId', '');
    }
    if (selectedRoles && !selectedManager) {
      form.setFieldValue('managedLocationIds', []);
    }
  }, [selectedRoles, form, selectedCashierOrAgent, selectedManager]);
  const isSelf = useIsSelf(initialValues?.email);
  const isManger = useIsManager();
  return (
    <Form form={form} layout="vertical" name={name} initialValues={initialValues}>
      <Form.Item name="firstName" label={dt('firstName')} rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="lastName" label={dt('lastName')} rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      {!edit && (
        <Form.Item name="email" label={t('email')} rules={[{ type: 'email' }]}>
          <Input />
        </Form.Item>
      )}
      <Form.Item
        name="phoneNumber"
        label={t('phoneNumber')}
        rules={[{ validator: phoneValidation }]}
      >
        <PhoneInput />
      </Form.Item>
      <Form.Item name={['document', 'type']} label={t('documentType')}>
        <IndividualDocumentTypeInput placeholder="" allowClear />
      </Form.Item>
      <Form.Item name={['document', 'number']} label={t('documentNumber')}>
        <Input />
      </Form.Item>
      <Typography.Title className="EmployeeShow-Title" level={5}>
        {t('authority')}
      </Typography.Title>
      <RolesInput initialRoles={initialValues?.roles} label={t('roles')} isSelf={isSelf} />
      <Form.Item
        name="terminalId"
        label={t('terminal')}
        rules={[
          {
            required: selectedCashierOrAgent && isLocationMandatory,
          },
        ]}
        hidden={!selectedCashierOrAgent}
      >
        <TerminalInput allowClear mPOSOnly />
      </Form.Item>
      <Form.Item
        name="workLocationId"
        label={t('cashierAgentAt')}
        rules={[
          {
            required: selectedCashierOrAgent && isLocationMandatory,
          },
        ]}
        hidden={!selectedCashierOrAgent}
      >
        <LocationInput allowClear={!isSelf} />
      </Form.Item>
      <Form.Item
        name="managedLocationIds"
        label={t('managesLocations')}
        rules={[{ required: selectedManager && isLocationMandatory }]}
        hidden={!selectedManager}
      >
        <LocationInput disabled={isManger} allowClear mode="multiple" maxTagCount="responsive" />
      </Form.Item>
      <Form.Item name={['authority', 'position']} label={t('position')}>
        <Input />
      </Form.Item>
      <Form.Item name="reportsTo" label={t('reportsTo')}>
        <EmployeeInput />
      </Form.Item>
      <Input.Group>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name={['authority', 'owner']} label={t('owner')} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['authority', 'executive']}
              label={t('executive')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Input.Group>
    </Form>
  );
};

export default EmployeeForm;
